/**
 * What the autopilot has actually done, run by run.
 *
 * Rows rather than a table: a run is an object with a time and an outcome, not
 * a column of figures anybody compares downwards. A failed run says so on its
 * own row, in the tone the rest of the CRM uses for a failure, instead of
 * leaving the operator to notice that one number in the last column is not
 * zero.
 */
import { AlertTriangle } from "lucide-react";
import { formatWhen } from "../../../../lib/siteScan";
import { noteKey, type AutopilotRun } from "../../../../lib/scanLeads";
import { StatusPill, fmtDateTime, fmtNum, listCard } from "../../../ui";
import type { T } from "../../site-scanner/types";

/** Started, trigger, checked, added, dropped, failed. */
const COLS = "1.1fr 1fr .7fr .7fr .7fr .7fr";

export function AutopilotHistory({ runs, t }: { runs: AutopilotRun[]; t: T }) {
  if (!runs.length) return null;

  return (
    // `crm-list-scroll`: this log is a guest in the autopilot's settings panel,
    // not the reason for it. Without its own scroller the panel takes the
    // list's 902px floor, and on a phone a swipe on the log drags the whole
    // panel - numbers, settings and all - sideways. See index.css.
    <section className="border-t border-[var(--crm-hair)] pt-4">
      <h3 className="crm-card-t px-[26px] pb-2 text-[15px]">{t("leads.autopilot.history")}</h3>
      <div className="crm-list-scroll pb-2">
        <div className="crm-list-head" style={{ ...listCard(COLS), gridTemplateColumns: COLS, paddingTop: 0 }}>
          <span>{t("leads.autopilot.run.started")}</span>
          <span>{t("leads.autopilot.run.trigger")}</span>
          <span>{t("leads.autopilot.run.checked")}</span>
          <span>{t("leads.autopilot.run.added")}</span>
          <span>{t("leads.autopilot.run.dropped")}</span>
          <span>{t("leads.autopilot.run.failed")}</span>
        </div>

        <div className="crm-rows pt-0" style={listCard(COLS)}>
          {runs.map((run) => (
            <div key={run.id} className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
              <span className="crm-tnum flex min-w-0 items-center gap-2 pl-3 text-[12.5px]" title={fmtDateTime(run.startedAt)}>
                {run.error ? <AlertTriangle className="size-3.5 shrink-0 text-[var(--color-destructive)]" /> : null}
                <span className="crm-trunc">{formatWhen(run.startedAt)}</span>
              </span>
              <span>
                <StatusPill tone={run.trigger === "manual" ? "info" : "mut"}>
                  {t(`leads.autopilot.trigger.${run.trigger}`)}
                </StatusPill>
              </span>
              {/* A run that had nothing to do says so in words. Four zeros in the
                  numeric columns is the same picture a broken autopilot draws,
                  and telling them apart was costing an operator their evening. */}
              {run.note ? (
                <span className="crm-mut crm-trunc text-[12.5px]" style={{ gridColumn: "3 / -1" }}>
                  {t(`leads.autopilot.note.${noteKey(run.note)}`)}
                </span>
              ) : (
                <>
                  <span className="crm-tnum text-[13px]">{fmtNum(run.checked)}</span>
                  {/* Added is the figure the run exists to produce, so it carries
                      the weight; the rest are context. */}
                  <span className="crm-tnum text-[13.25px] font-bold">{fmtNum(run.added)}</span>
                  <span className="crm-mut crm-tnum text-[13px]">{fmtNum(run.dropped)}</span>
                  <span
                    className={
                      run.failed > 0
                        ? "crm-tnum text-[13px] font-bold text-[var(--color-destructive)]"
                        : "crm-mut crm-tnum text-[13px]"
                    }
                  >
                    {fmtNum(run.failed)}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
